import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Table, Badge, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import AIConversation from '../components/AIConversation';
import BackToDashboard from '../components/BackToDashboard';

function ProductCatalog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // Updated stages: entry, conversation, distilling, review, customizing
  const [stage, setStage] = useState('entry');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Product data
  const [hasOtherProducts, setHasOtherProducts] = useState(null);
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [finalCatalog, setFinalCatalog] = useState([]);
  const [targetAudience, setTargetAudience] = useState('');

  // Conversation data
  const [initialMessage, setInitialMessage] = useState('');
  const [strategySummary, setStrategySummary] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load target audience for context
    try {
      const response = await fetch('/api/business/field/target_audience', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.field_value) {
          setTargetAudience(data.field_value);
        }
      }
    } catch (err) {
      // Not critical, continue
    }

    // Load core product and pricing data and pre-populate it
    try {
      const response = await fetch('/api/business/fields/by-names', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          field_names: ['core_product', 'pricing_data']
        })
      });

      if (response.ok) {
        const data = await response.json();
        const coreProduct = data.fields?.core_product || '';
        const pricingDataStr = data.fields?.pricing_data || '';

        if (coreProduct) {
          let salePrice = '';
          let costToDeliver = '';
          let purchaseType = 'one-time';
          let productType = 'digital';

          // Parse pricing data if available
          if (pricingDataStr) {
            try {
              const pricingData = JSON.parse(pricingDataStr);

              // Determine price and cost based on pricing model
              if (pricingData.pricing_model === 'monthly') {
                salePrice = pricingData.monthly_price || '';
                costToDeliver = pricingData.cost_to_deliver || '';
                purchaseType = 'recurring-monthly';
              } else if (pricingData.pricing_model === 'annual') {
                salePrice = pricingData.annual_price || '';
                costToDeliver = pricingData.cost_to_deliver || '';
                purchaseType = 'recurring-annual';
              } else if (pricingData.pricing_model === 'quarterly') {
                salePrice = pricingData.quarterly_price || '';
                costToDeliver = pricingData.cost_to_deliver || '';
                purchaseType = 'recurring-monthly'; // Closest approximation
              } else if (pricingData.pricing_model === 'one-time') {
                salePrice = pricingData.one_time_price || '';
                costToDeliver = pricingData.cost_to_deliver || '';
                purchaseType = 'one-time';
              } else if (pricingData.pricing_model === 'hybrid') {
                // For hybrid, use the monthly component
                salePrice = pricingData.hybrid_monthly || '';
                costToDeliver = pricingData.hybrid_monthly_cost || '';
                purchaseType = 'recurring-monthly';
              }
            } catch (err) {
              console.error('Error parsing pricing data:', err);
            }
          }

          // Calculate profit per sale
          const profit = salePrice && costToDeliver ?
            parseFloat(salePrice) - parseFloat(costToDeliver) : 0;

          // Pre-populate first product with core product data
          setProducts([{
            id: Date.now(),
            name: coreProduct,
            type: productType,
            purchase_type: purchaseType,
            sale_price: salePrice,
            cost_to_deliver: costToDeliver,
            profit_per_sale: profit,
            monthly_sales: '',
            monthly_churn_rate: '',
            appeals_to_target: 'yes',
            notes: ''
          }]);
        }
      }
    } catch (err) {
      console.error('Error loading core product:', err);
      // Not critical, continue
    }

    // Load saved products if they exist
    try {
      const response = await fetch('/api/products/get-catalog', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded catalog data:', data);
        if (data.catalog && data.catalog.length > 0) {
          // Convert database products to form format
          const loadedProducts = data.catalog.map(p => ({
            id: p.id,
            name: p.name || '',
            type: p.type || 'digital',
            purchase_type: p.purchase_type || 'one-time',
            sale_price: parseFloat(p.sale_price) || '',
            cost_to_deliver: parseFloat(p.cost_to_deliver) || '',
            profit_per_sale: parseFloat(p.profit_per_sale) || 0,
            monthly_sales: p.monthly_sales || '',
            monthly_churn_rate: p.monthly_churn_rate ? parseFloat(p.monthly_churn_rate) : '',
            appeals_to_target: p.appeals_to_target || 'yes',
            is_primary_tripwire: p.is_primary_tripwire || false,
            notes: p.notes || '',
            position: p.position || '',
            product_group: p.product_group || '',
            reasoning: p.reasoning || '',
            override_notes: p.override_notes || ''
          }));
          console.log('Setting products:', loadedProducts);
          setProducts(loadedProducts);
          // Default to "yes" if products exist (e.g., from quick-start) OR if explicitly saved as true
          const hasProducts = loadedProducts.length > 0 || data.has_other_products === true || data.has_other_products === 'true';
          setHasOtherProducts(hasProducts);
          console.log('Has other products:', hasProducts, '(loaded:', loadedProducts.length, ', saved:', data.has_other_products, ')');
          setStage('entry'); // Go straight to product entry view
        } else {
          console.log('No catalog found in response');
        }
      } else {
        console.log('Failed to fetch catalog, status:', response.status);
      }
    } catch (err) {
      console.error('Error loading saved products:', err);
      // Not critical, continue with pre-populated core product
    }

    setLoading(false);
  };

  const handleAddProduct = () => {
    if (products.length >= 25) {
      setError('Maximum 25 products allowed');
      return;
    }

    setProducts([...products, {
      id: Date.now(),
      name: '',
      type: 'digital',
      purchase_type: 'one-time',
      sale_price: '',
      cost_to_deliver: '',
      profit_per_sale: 0,
      monthly_sales: '',
      monthly_churn_rate: '',
      appeals_to_target: 'yes',
      is_primary_tripwire: false,
      notes: ''
    }]);
  };

  const handleRemoveProduct = (id) => {
    setProducts(products.filter(p => p.id !== id));
  };

  const handleProductChange = (id, field, value) => {
    const updatedProducts = products.map(p => {
      if (p.id === id) {
        const updated = { ...p, [field]: value };

        // Calculate profit if price or cost changes
        if (field === 'sale_price' || field === 'cost_to_deliver') {
          const price = parseFloat(field === 'sale_price' ? value : updated.sale_price) || 0;
          const cost = parseFloat(field === 'cost_to_deliver' ? value : updated.cost_to_deliver) || 0;
          updated.profit_per_sale = price - cost;
        }

        return updated;
      }
      // If setting primary tripwire, unset it on other products
      if (field === 'is_primary_tripwire' && value === true) {
        return { ...p, is_primary_tripwire: false };
      }
      return p;
    });
    setProducts(updatedProducts);
  };

  const handleContinueToAnalysis = async () => {
    setError('');

    // Validate products
    const validProducts = products.filter(p => p.name.trim() && p.sale_price);
    if (validProducts.length === 0) {
      setError('Please add at least one product with a name and price');
      return;
    }

    setStage('analyzing');

    try {
      // Save products immediately so user doesn't lose work
      const saveResponse = await fetch('/api/products/save-initial-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          products: validProducts
        })
      });

      if (!saveResponse.ok) {
        const saveError = await saveResponse.json();
        setError(`Failed to save products: ${saveError.error || 'Unknown error'}`);
        setStage('entry');
        return;
      }

      // If products already have positions from a previous AI analysis, skip to review
      const hasPositions = validProducts.some(p => p.position && p.position !== '');
      if (hasPositions) {
        setFinalCatalog(validProducts);
        setStage('review');
        return;
      }

      // Start the AI conversation instead of direct analysis
      const response = await fetch('/api/products/start-conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          products: validProducts
        })
      });

      if (response.ok) {
        const data = await response.json();
        setInitialMessage(data.message);
        setStage('conversation');
      } else {
        const errorData = await response.json();
        setError(`Failed to start conversation: ${errorData.error || 'Unknown error'}`);
        setStage('entry');
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
      setStage('entry');
    }
  };

  // Handle sending a message in the conversation
  const handleConversationMessage = async (message, history) => {
    try {
      const validProducts = products.filter(p => p.name.trim() && p.sale_price);

      const response = await fetch('/api/products/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message,
          history,
          products: validProducts
        })
      });

      if (response.ok) {
        const data = await response.json();
        return {
          message: data.message,
          isComplete: data.isComplete
        };
      } else {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to get response' };
      }
    } catch (err) {
      return { error: 'Error communicating with AI: ' + err.message };
    }
  };

  // Handle conversation completion - distill into ladder and summary
  const handleConversationComplete = async (history) => {
    setStage('distilling');
    setError('');

    try {
      const validProducts = products.filter(p => p.name.trim() && p.sale_price);

      const response = await fetch('/api/products/distill-conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          history,
          products: validProducts
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Merge distilled ladder with original product data
        const mergedCatalog = data.ladder.map(ladderProduct => {
          const originalProduct = validProducts.find(p => p.name === ladderProduct.name);
          return {
            ...ladderProduct,
            // Preserve original fields
            id: originalProduct?.id,
            is_primary_tripwire: originalProduct?.is_primary_tripwire || false
          };
        });

        setFinalCatalog(mergedCatalog);
        setStrategySummary(data.strategy_summary || '');
        setRecommendations({ summary: data.summary_text });
        setStage('review');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate recommendations: ${errorData.error || 'Unknown error'}`);
        setStage('conversation');
      }
    } catch (err) {
      setError('Error generating recommendations: ' + err.message);
      setStage('conversation');
    }
  };

  // Handle going back to conversation from review - start fresh if no history
  const handleBackToConversation = async () => {
    if (initialMessage) {
      // Conversation already in progress, just go back
      setStage('conversation');
      return;
    }

    // No conversation history — start a fresh one
    setStage('analyzing');
    try {
      const validProducts = products.filter(p => p.name.trim() && p.sale_price);
      const response = await fetch('/api/products/start-conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ products: validProducts })
      });

      if (response.ok) {
        const data = await response.json();
        setInitialMessage(data.message);
        setStage('conversation');
      } else {
        setError('Failed to start conversation');
        setStage('review');
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
      setStage('review');
    }
  };

  const handleCustomize = () => {
    setStage('customizing');
  };

  const handleCatalogChange = (index, field, value) => {
    const updated = [...finalCatalog];
    updated[index][field] = value;
    setFinalCatalog(updated);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...finalCatalog];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setFinalCatalog(updated);
  };

  const handleMoveDown = (index) => {
    if (index === finalCatalog.length - 1) return;
    const updated = [...finalCatalog];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setFinalCatalog(updated);
  };

  const handleRemoveFromLadder = (index) => {
    const updated = finalCatalog.filter((_, i) => i !== index);
    setFinalCatalog(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/products/save-catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          has_other_products: hasOtherProducts,
          catalog: finalCatalog,
          all_products: products  // Send all original products so they're kept
        })
      });

      if (response.ok) {
        // Navigate back to dashboard
        navigate('/dashboard/business-model');
      } else {
        const errorData = await response.json();
        setError(`Failed to save catalog: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving catalog: ' + err.message);
    }

    setSaving(false);
  };

  // Save catalog with strategy summary
  const handleSaveCatalog = async () => {
    setSaving(true);
    setError('');

    try {
      // First save the strategy summary to business_data
      if (strategySummary) {
        await fetch('/api/business/field', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            field_name: 'product_strategy_summary',
            field_value: strategySummary
          })
        });
      }

      // Then save the catalog
      const response = await fetch('/api/products/save-catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          has_other_products: hasOtherProducts,
          catalog: finalCatalog,
          all_products: products
        })
      });

      if (response.ok) {
        navigate('/dashboard/business-model');
      } else {
        const errorData = await response.json();
        setError(`Failed to save catalog: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving catalog: ' + err.message);
    }

    setSaving(false);
  };

  const getPositionBadge = (position) => {
    const badges = {
      'entry': { bg: 'success', icon: '🎣', label: 'Entry/Tripwire' },
      'core': { bg: 'primary', icon: '⭐', label: 'Core Offer' },
      'upsell': { bg: 'info', icon: '⬆️', label: 'Upsell' },
      'cross-sell': { bg: 'secondary', icon: '↔️', label: 'Cross-sell' },
      'premium': { bg: 'warning', icon: '💎', label: 'Premium' },
      'downsell': { bg: 'light', icon: '⬇️', label: 'Downsell' }
    };
    const badge = badges[position] || badges.core;
    return (
      <Badge bg={badge.bg}>
        {badge.icon} {badge.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // Screen 1: Product Entry
  if (stage === 'entry') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Product Catalog & Pricing</h2>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {targetAudience && (
                  <Alert variant="primary" className="mb-3">
                    <strong>Your Target Audience:</strong> {targetAudience}
                  </Alert>
                )}

                <Alert variant="info" className="mb-4">
                  <strong>Let's map out all your products and services</strong> so we can build your strategic value ladder.
                </Alert>

                <Form.Group className="mb-4">
                  <Form.Label className="h5">Do you sell other products or services?</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Yes"
                      name="hasProducts"
                      checked={hasOtherProducts === true}
                      onChange={() => setHasOtherProducts(true)}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="No"
                      name="hasProducts"
                      checked={hasOtherProducts === false}
                      onChange={() => setHasOtherProducts(false)}
                    />
                  </div>
                </Form.Group>

                {hasOtherProducts === true && (
                  <>
                    <div className="mb-3">
                      <Button variant="primary" onClick={handleAddProduct}>
                        + Add Product
                      </Button>
                      <span className="text-muted ms-3 small">
                        {products.length} / 25 products
                      </span>
                    </div>

                    {products.length > 0 && (
                      <div className="table-responsive">
                        <Table bordered>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '11%' }}>Product Name</th>
                              <th style={{ width: '8%' }}>Type</th>
                              <th style={{ width: '8%' }}>Purchase Type</th>
                              <th style={{ width: '7%' }}>Sale Price</th>
                              <th style={{ width: '7%' }}>Cost to Deliver</th>
                              <th style={{ width: '6%' }}>Profit</th>
                              <th style={{ width: '7%' }} title="Enter 0 if not launched yet">Monthly Sales</th>
                              <th style={{ width: '7%' }} title="Monthly churn rate for subscriptions (5-7% typical)">Churn %</th>
                              <th style={{ width: '9%' }}>Appeals to Target?</th>
                              <th style={{ width: '8%' }} title="Entry point for organic customers - pick ONE">Primary Tripwire</th>
                              <th style={{ width: '10%' }}>Notes</th>
                              <th style={{ width: '4%' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((product, index) => (
                              <tr key={product.id}>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={product.name}
                                    onChange={(e) => handleProductChange(product.id, 'name', e.target.value)}
                                    placeholder="Product name"
                                  />
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={product.product_group || ''}
                                    onChange={(e) => handleProductChange(product.id, 'product_group', e.target.value)}
                                    placeholder="Product group (e.g., Command Center)"
                                    className="mt-1"
                                    style={{ fontSize: '0.75rem', color: '#666' }}
                                    title="Products with the same group are treated as tiers of one product (for memes, campaigns, etc.)"
                                  />
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    value={product.type}
                                    onChange={(e) => handleProductChange(product.id, 'type', e.target.value)}
                                  >
                                    <option value="digital">Digital</option>
                                    <option value="physical">Physical</option>
                                    <option value="service">Service</option>
                                  </Form.Select>
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    value={product.purchase_type}
                                    onChange={(e) => handleProductChange(product.id, 'purchase_type', e.target.value)}
                                  >
                                    <option value="one-time">One-Time</option>
                                    <option value="recurring-monthly">Monthly</option>
                                    <option value="recurring-annual">Annual</option>
                                  </Form.Select>
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={product.sale_price}
                                    onChange={(e) => handleProductChange(product.id, 'sale_price', e.target.value)}
                                    placeholder="$"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={product.cost_to_deliver}
                                    onChange={(e) => handleProductChange(product.id, 'cost_to_deliver', e.target.value)}
                                    placeholder="$"
                                  />
                                </td>
                                <td>
                                  <div className="small pt-2">
                                    ${product.profit_per_sale.toFixed(2)}
                                  </div>
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={product.monthly_sales}
                                    onChange={(e) => handleProductChange(product.id, 'monthly_sales', e.target.value)}
                                    placeholder="#"
                                  />
                                </td>
                                <td>
                                  {(product.purchase_type === 'recurring-monthly' || product.purchase_type === 'recurring-annual') ? (
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="100"
                                      value={product.monthly_churn_rate}
                                      onChange={(e) => handleProductChange(product.id, 'monthly_churn_rate', e.target.value)}
                                      placeholder="5-7"
                                      title="Industry standard: 5-7% monthly churn for SaaS. Enter number only (no % sign): e.g. 6 for 6%"
                                    />
                                  ) : (
                                    <div className="small pt-2 text-muted text-center">N/A</div>
                                  )}
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    value={product.appeals_to_target}
                                    onChange={(e) => handleProductChange(product.id, 'appeals_to_target', e.target.value)}
                                  >
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                    <option value="maybe">Maybe</option>
                                  </Form.Select>
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="radio"
                                    name="primaryTripwire"
                                    checked={product.is_primary_tripwire || false}
                                    onChange={(e) => handleProductChange(product.id, 'is_primary_tripwire', e.target.checked)}
                                    title="Set as primary tripwire - organic customers will enter through this product"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    as="textarea"
                                    rows={1}
                                    value={product.notes}
                                    onChange={(e) => handleProductChange(product.id, 'notes', e.target.value)}
                                    placeholder="Notes"
                                  />
                                </td>
                                <td className="text-center">
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleRemoveProduct(product.id)}
                                  >
                                    ×
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}

                {(hasOtherProducts === false || (hasOtherProducts === true && products.length > 0)) && (
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    {products.some(p => p.position) && (
                      <Button
                        variant="outline-dark"
                        size="lg"
                        disabled={saving}
                        onClick={async () => {
                          setSaving(true);
                          setError('');
                          try {
                            const validProducts = products.filter(p => p.name.trim() && p.sale_price);
                            const response = await fetch('/api/products/save-initial-products', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ products: validProducts })
                            });
                            if (!response.ok) {
                              const err = await response.json();
                              setError(err.error || 'Save failed');
                            } else {
                              navigate('/dashboard/business-model');
                            }
                          } catch (err) {
                            setError(err.message);
                          }
                          setSaving(false);
                        }}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleContinueToAnalysis}
                    >
                      Save & Continue →
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 2: Analyzing/Starting conversation (loading state)
  if (stage === 'analyzing') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Preparing Your Strategy Conversation...</h4>
              <p className="text-muted mt-3">
                Our AI advisor is reviewing your products and preparing to discuss your value ladder strategy.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 2b: AI Conversation
  if (stage === 'conversation') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            <AIConversation
              title="Product Strategy Conversation"
              subtitle="Let's discuss your product lineup from both a marketing and investor perspective."
              initialMessage={initialMessage}
              onSendMessage={handleConversationMessage}
              onComplete={handleConversationComplete}
              onCancel={() => setStage('entry')}
              maxExchanges={10}
              doneButtonText="I'm Done - Generate Strategy"
              placeholder="Share your thoughts..."
            />
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 2c: Distilling conversation (loading state)
  if (stage === 'distilling') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Generating Your Value Ladder & Strategy...</h4>
              <p className="text-muted mt-3">
                Analyzing our conversation to create your optimized value ladder and strategy summary.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3: Review - Value Ladder + Strategy Summary
  if (stage === 'review' && finalCatalog.length > 0) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">📊 Your Product Strategy</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  <strong>Strategy Complete!</strong> Based on our conversation, here's your value ladder
                  and the strategic context that will be used in your exit strategy analysis.
                </Alert>

                {/* Strategy Summary - Editable */}
                <Card className="mb-4 border-primary">
                  <Card.Header className="bg-primary text-white">
                    <strong>📝 Product Strategy Summary</strong>
                    <small className="d-block mt-1">This will be used as context for your exit strategy analysis. Edit if needed.</small>
                  </Card.Header>
                  <Card.Body>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={strategySummary}
                      onChange={(e) => setStrategySummary(e.target.value)}
                      placeholder="Strategy summary from the conversation..."
                    />
                  </Card.Body>
                </Card>

                {/* Value Ladder */}
                <h5 className="mb-3">Recommended Value Ladder:</h5>
                <div className="table-responsive">
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px' }}>#</th>
                        <th>Product</th>
                        <th style={{ width: '180px' }}>Position</th>
                        <th style={{ width: '100px' }}>Price</th>
                        <th>Reasoning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalCatalog.map((product, index) => (
                        <tr key={index}>
                          <td className="text-center"><strong>{index + 1}</strong></td>
                          <td>
                            <strong>{product.name}</strong>
                            {product.is_primary_tripwire && (
                              <Badge bg="warning" className="ms-2">Primary Tripwire</Badge>
                            )}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={product.position || ''}
                              onChange={(e) => handleCatalogChange(index, 'position', e.target.value)}
                            >
                              <option value="">-- Select --</option>
                              <option value="entry">Entry/Tripwire</option>
                              <option value="core">Core Offer</option>
                              <option value="upsell">Upsell</option>
                              <option value="cross-sell">Cross-sell</option>
                              <option value="premium">Premium</option>
                            </Form.Select>
                          </td>
                          <td>${parseFloat(product.sale_price || 0).toFixed(2)}</td>
                          <td className="small text-muted">{product.reasoning}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBackToConversation}
                  >
                    ← Back to Conversation
                  </Button>
                  <div>
                    <Button
                      variant="outline-primary"
                      onClick={handleCustomize}
                      className="me-2"
                    >
                      Customize Order
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSaveCatalog}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save & Continue →'}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3 (Legacy): AI Recommendations - keeping for backward compatibility
  if (stage === 'recommendations' && recommendations) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">💡 Recommended Value Ladder</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  <strong>AI Analysis Complete!</strong> Based on pricing, profit margins, target market fit, and sales volume,
                  here's your recommended value ladder structure:
                </Alert>

                {recommendations.summary && (
                  <Card className="bg-light mb-4">
                    <Card.Body>
                      <h5>Strategic Overview:</h5>
                      <p className="mb-0">{recommendations.summary}</p>
                    </Card.Body>
                  </Card>
                )}

                <h5 className="mb-3">Recommended Promotion Sequence:</h5>
                <div className="table-responsive">
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '20%' }}>Product</th>
                        <th style={{ width: '15%' }}>Position</th>
                        <th style={{ width: '12%' }}>Price</th>
                        <th style={{ width: '12%' }}>Profit/Sale</th>
                        <th style={{ width: '36%' }}>AI Reasoning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalCatalog.map((product, index) => (
                        <tr key={index}>
                          <td className="text-center">{index + 1}</td>
                          <td><strong>{product.name}</strong></td>
                          <td>{getPositionBadge(product.position)}</td>
                          <td>${parseFloat(product.sale_price).toFixed(2)}</td>
                          <td className="text-success">${product.profit_per_sale.toFixed(2)}</td>
                          <td className="small">{product.reasoning}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('entry')}
                  >
                    ← Back to Edit Products
                  </Button>
                  <div>
                    <Button
                      variant="outline-primary"
                      className="me-2"
                      onClick={handleCustomize}
                    >
                      Customize Order
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Accept & Save →'}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 4: User Customization
  if (stage === 'customizing') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">✏️ Customize Your Value Ladder</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  Adjust the order and positioning of your products. Use the arrows to reorder the promotion sequence.
                </Alert>

                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '5%' }}>Order</th>
                        <th style={{ width: '18%' }}>Product</th>
                        <th style={{ width: '14%' }}>Position</th>
                        <th style={{ width: '14%' }}>Product Group</th>
                        <th style={{ width: '14%' }}>Purchase Type</th>
                        <th style={{ width: '18%' }}>Override Notes</th>
                        <th style={{ width: '9%' }}>Reorder</th>
                        <th style={{ width: '8%' }}>Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalCatalog.map((product, index) => (
                        <tr key={index}>
                          <td className="text-center"><strong>{index + 1}</strong></td>
                          <td>{product.name}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={product.position}
                              onChange={(e) => handleCatalogChange(index, 'position', e.target.value)}
                            >
                              <option value="entry">🎣 Entry/Tripwire</option>
                              <option value="core">⭐ Core Offer</option>
                              <option value="upsell">⬆️ Upsell</option>
                              <option value="cross-sell">↔️ Cross-sell</option>
                              <option value="premium">💎 Premium</option>
                              <option value="downsell">⬇️ Downsell</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={product.product_group || ''}
                              onChange={(e) => handleCatalogChange(index, 'product_group', e.target.value)}
                              placeholder="e.g., Command Center"
                              title="Products with the same group name are treated as tiers of one product"
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={product.purchase_type || 'one-time'}
                              onChange={(e) => handleCatalogChange(index, 'purchase_type', e.target.value)}
                            >
                              <option value="one-time">One-Time</option>
                              <option value="recurring-monthly">Recurring (Monthly)</option>
                              <option value="recurring-annual">Recurring (Annual)</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              as="textarea"
                              rows={1}
                              value={product.override_notes || ''}
                              onChange={(e) => handleCatalogChange(index, 'override_notes', e.target.value)}
                              placeholder="Why did you override AI recommendation?"
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              className="me-1"
                            >
                              ↑
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === finalCatalog.length - 1}
                            >
                              ↓
                            </Button>
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveFromLadder(index)}
                              title="Remove from value ladder"
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('recommendations')}
                  >
                    ← Back to Recommendations
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save My Value Ladder →'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default ProductCatalog;
